import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { DataGrid, GridToolbarContainer, GridToolbarFilterButton } from '@mui/x-data-grid';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ClearAllOutlinedIcon from '@mui/icons-material/ClearAllOutlined';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { tokens } from '../../theme';
import Header from '../../components/Header';
import { can } from '../../utils/permissions';

function SummaryToolbar() {
  return (
    <GridToolbarContainer sx={{ px: 1, py: 0.5 }}>
      <GridToolbarFilterButton />
    </GridToolbarContainer>
  );
}

const inputSx = (colors) => ({
  minWidth: 180,
  backgroundColor: colors.primary[400],
  '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[700] },
});

const ExcursionSummaryReport = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const token = localStorage.getItem('token');
  const adminUser = useMemo(() => {
    try {
      const raw = localStorage.getItem('adminUser');
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }, []);
  const isCoordinator = Number(adminUser?.role) === 3;

  const [countries, setCountries] = useState([]);
  const [excursions, setExcursions] = useState([]);
  const [coordinators, setCoordinators] = useState([]);

  const [countryId, setCountryId] = useState('');
  const [coordinatorId, setCoordinatorId] = useState('');
  const [excursionId, setExcursionId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const filteredExcursions = useMemo(() => {
    if (!countryId) return excursions;
    return excursions.filter((e) => String(e.CountryID) === String(countryId));
  }, [excursions, countryId]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [aTotalPersons, setATotalPersons] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  const buildQuery = useCallback(() => {
    const params = new URLSearchParams();
    if (countryId) params.set('countryId', countryId);
    if (excursionId) params.set('excursionId', excursionId);
    if (startDate && endDate) {
      params.set('startDate', startDate);
      params.set('endDate', endDate);
    } else if (startDate) {
      params.set('startDate', startDate);
    }
    if (!isCoordinator && coordinatorId) {
      params.set('coordinatorId', coordinatorId);
    }
    return params.toString();
  }, [countryId, excursionId, startDate, endDate, isCoordinator, coordinatorId]);

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [countryRes, excursionRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_APILINK}/country`, { headers: { token } }),
          fetch(`${process.env.REACT_APP_APILINK}/excursion?page=1&limit=2000&sortBy=ExcursionID&sortOrder=ASC&filter={}`, {
            headers: { token },
          }),
        ]);
        const countriesData = await countryRes.json();
        const excursionsData = await excursionRes.json();
        setCountries(Array.isArray(countriesData) ? countriesData : []);
        setExcursions(Array.isArray(excursionsData?.excursions) ? excursionsData.excursions : []);
      } catch (e) {
        console.error('Failed to load lookup data for excursion summary report', e);
      }
    };

    const loadCoordinators = async () => {
      if (isCoordinator) return;
      try {
        const res = await fetch(`${process.env.REACT_APP_APILINK}/api/admin/coordinator-reports`, {
          headers: { token },
        });
        const data = await res.json();
        if (data?.success && Array.isArray(data.data)) {
          setCoordinators(
            data.data.map((row) => ({
              id: row.coordinatorId,
              name: row.coordinatorUsername,
            }))
          );
        }
      } catch (e) {
        console.error('Failed to load coordinators for excursion summary report', e);
      }
    };

    loadLookups();
    loadCoordinators();
  }, [token, isCoordinator]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const qs = buildQuery();
      const params = new URLSearchParams(qs);
      params.set('page', page);
      params.set('limit', pageSize);
      const res = await fetch(
        `${process.env.REACT_APP_APILINK}/api/admin/trip-reports/excursion-summary?${params.toString()}`,
        {
          headers: { token },
        }
      );
      const data = await res.json();
      if (!data.success) {
        setRows([]);
        setATotalPersons(0);
        setTotalCount(0);
        setError(data.message || 'Failed to load excursion summary report');
        return;
      }
      setRows(Array.isArray(data.data) ? data.data : []);
      setATotalPersons(Number(data.summary?.aTotalPersons || 0));
      setTotalCount(Number(data.pagination?.total || 0));
    } catch (e) {
      setRows([]);
      setATotalPersons(0);
      setTotalCount(0);
      setError(e.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }, [buildQuery, token, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [buildQuery]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const handleExport = async () => {
    setExporting(true);
    setError('');
    try {
      const qs = buildQuery();
      const res = await fetch(
        `${process.env.REACT_APP_APILINK}/api/admin/trip-reports/excursion-summary/export-excel${qs ? `?${qs}` : ''}`,
        {
          headers: { token },
        }
      );
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        setError(errBody.message || `Export failed (${res.status})`);
        return;
      }
      const blob = await res.blob();
      const dispo = res.headers.get('Content-Disposition');
      let filename = 'excursion-summary-report.xlsx';
      if (dispo && dispo.includes('filename=')) {
        const match = dispo.match(/filename="?([^";]+)"?/);
        if (match) filename = match[1];
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    setCountryId('');
    setExcursionId('');
    setStartDate('');
    setEndDate('');
    if (!isCoordinator) {
      setCoordinatorId('');
    }
  };

  const selectedExcursion = excursions.find((e) => String(e.ExcursionID) === String(excursionId));

  const columns = [
    { field: 'serialNo', headerName: 'Serial No', width: 95, headerAlign: 'right', align: 'right' },
    { field: 'destination', headerName: 'Destination', minWidth: 130, flex: 1 },
    { field: 'departureDateLabel', headerName: 'Departure Date', minWidth: 135, flex: 0.9 },
    { field: 'endDateLabel', headerName: 'End Date', minWidth: 120, flex: 0.9 },
    { field: 'guide', headerName: 'Guide', minWidth: 140, flex: 1 },
    { field: 'accommodation', headerName: 'Accommodation', minWidth: 155, flex: 1.1 },
    { field: 'busNo', headerName: 'Bus No', minWidth: 90, flex: 0.65 },
    { field: 'excursionName', headerName: 'Excursion', minWidth: 190, flex: 1.3 },
    { field: 'noOfPersons', headerName: 'No of Persons', width: 120, type: 'number', headerAlign: 'right', align: 'right' },
  ];

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
        <Header
          title="EXCURSION SUMMARY REPORT"
          subtitle="Report 1: destination-wise excursion persons (including package + individual sales)"
        />
        {can('reports.excursion_summary.export') && (
          <Button
            variant="contained"
            startIcon={<FileDownloadOutlinedIcon />}
            disabled={exporting}
            onClick={handleExport}
            sx={{ backgroundColor: colors.greenAccent[600], '&:hover': { backgroundColor: colors.greenAccent[700] } }}
          >
            {exporting ? 'Exporting...' : 'Export Excel'}
          </Button>
        )}
      </Box>

      <Box display="flex" flexWrap="wrap" gap={2} alignItems="flex-start" mb={2}>
        {!isCoordinator ? (
          <FormControl size="small" variant="outlined" sx={{ ...inputSx(colors), minWidth: 220 }}>
            <InputLabel id="esr-coordinator" shrink>
              Coordinator
            </InputLabel>
            <Select
              labelId="esr-coordinator"
              label="Coordinator"
              value={coordinatorId}
              displayEmpty
              notched
              onChange={(e) => setCoordinatorId(String(e.target.value))}
              renderValue={(selected) => {
                if (!selected) return <Box component="span" sx={{ fontStyle: 'italic', opacity: 0.92 }}>All coordinators</Box>;
                const c = coordinators.find((x) => String(x.id) === String(selected));
                return c?.name || String(selected);
              }}
            >
              <MenuItem value="">
                <em>All coordinators</em>
              </MenuItem>
              {coordinators.map((coord) => (
                <MenuItem key={coord.id} value={String(coord.id)}>
                  {coord.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : null}

        <FormControl size="small" variant="outlined" sx={inputSx(colors)}>
          <InputLabel id="esr-country" shrink>
            Destination
          </InputLabel>
          <Select
            labelId="esr-country"
            label="Destination"
            value={countryId}
            displayEmpty
            notched
            onChange={(e) => { setCountryId(String(e.target.value)); setExcursionId(''); }}
            MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
            renderValue={(selected) => {
              if (!selected) return <Box component="span" sx={{ fontStyle: 'italic', opacity: 0.92 }}>All destinations</Box>;
              const c = countries.find((x) => String(x.CountryID) === String(selected));
              return c?.CountryName || String(selected);
            }}
          >
            <MenuItem value="">
              <em>All destinations</em>
            </MenuItem>
            {countries.map((country) => (
              <MenuItem key={country.CountryID} value={String(country.CountryID)}>
                {country.CountryName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" variant="outlined" sx={{ ...inputSx(colors), minWidth: 230 }}>
          <InputLabel id="esr-excursion" shrink>
            Excursion
          </InputLabel>
          <Select
            labelId="esr-excursion"
            label="Excursion"
            value={excursionId}
            displayEmpty
            notched
            onChange={(e) => setExcursionId(String(e.target.value))}
            MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
            renderValue={(selected) => {
              if (!selected) return <Box component="span" sx={{ fontStyle: 'italic', opacity: 0.92 }}>All excursions</Box>;
              const ex = excursions.find((x) => String(x.ExcursionID) === String(selected));
              return ex?.ExcursionName || String(selected);
            }}
          >
            <MenuItem value="">
              <em>All excursions</em>
            </MenuItem>
            {filteredExcursions.map((excursion) => (
              <MenuItem key={excursion.ExcursionID} value={String(excursion.ExcursionID)}>
                {excursion.ExcursionName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          label="Start date"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ ...inputSx(colors), minWidth: 150 }}
        />

        <TextField
          size="small"
          label="End date"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ ...inputSx(colors), minWidth: 150 }}
        />

        <Button
          variant="outlined"
          startIcon={<ClearAllOutlinedIcon />}
          onClick={clearFilters}
          sx={{
            alignSelf: 'center',
            borderColor: colors.grey[500],
            color: colors.grey[100],
            '&:hover': { borderColor: colors.grey[400], backgroundColor: `${colors.primary[500]}88` },
          }}
        >
          Clear filters
        </Button>
      </Box>

      <Box mb={2} p={2} sx={{ backgroundColor: colors.primary[400], borderRadius: '8px' }}>
        <Typography variant="h5" fontWeight="bold">
          A = {aTotalPersons}
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9 }}>
          Total persons for current filter{selectedExcursion ? ` (${selectedExcursion.ExcursionName})` : ''}.
        </Typography>
      </Box>

      {error ? (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      ) : null}

      <Box
        height="640px"
        sx={{
          '& .MuiDataGrid-root': { border: 'none' },
          '& .MuiDataGrid-cell': { borderBottom: 'none' },
          '& .MuiDataGrid-columnHeaders': { backgroundColor: colors.blueAccent[700], borderBottom: 'none' },
          '& .MuiDataGrid-virtualScroller': { backgroundColor: colors.primary[400] },
          '& .MuiDataGrid-footerContainer': { borderTop: 'none', backgroundColor: colors.blueAccent[700] },
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          components={{ Toolbar: SummaryToolbar }}
          loading={loading}
          disableSelectionOnClick
          paginationMode="server"
          page={page - 1}
          pageSize={pageSize}
          rowCount={totalCount}
          rowsPerPageOptions={[25, 50, 100]}
          onPageChange={(p) => setPage(p + 1)}
          onPageSizeChange={(s) => {
            setPageSize(s);
            setPage(1);
          }}
        />
      </Box>
    </Box>
  );
};

export default ExcursionSummaryReport;
